<template>
  <div class="flex items-center justify-center space-x-2">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      :ref="(el) => (inputs[index] = el)"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      autocomplete="off"
      class="w-12 h-14 text-center text-2xl font-semibold border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark/20 focus:border-dark transition-colors"
      :class="[digits[index] ? 'border-dark bg-gray-50' : 'border-gray-300 bg-white']"
      @input="handleInput(index, $event)"
      @keydown="handleKeyDown(index, $event)"
      @focus="handleFocus(index, $event)"
      @paste="handlePaste" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from "vue"

const emit = defineEmits(["complete", "update:modelValue"])
const props = defineProps({
  length: {
    type: Number,
    default: 6,
  },
  modelValue: String,
})

const digits = ref(Array(props.length).fill(""))
const inputs = ref([])

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      const newDigits = newVal.split("").concat(Array(props.length).fill("")).slice(0, props.length)
      digits.value = newDigits
      // Update input values
      nextTick(() => {
        inputs.value.forEach((input, idx) => {
          if (input) input.value = newDigits[idx] || ""
        })
      })
    } else {
      digits.value = Array(props.length).fill("")
      nextTick(() => {
        inputs.value.forEach((input) => {
          if (input) input.value = ""
        })
      })
    }
  }
)

const handleFocus = (index, event) => {
  // Select the content on focus for easy replacement
  event.target.select()
}

const handleInput = (index, event) => {
  const input = event.target
  let value = input.value

  // Remove any non-digit characters
  value = value.replace(/\D/g, "")

  // Handle multiple digits (e.g., from autocomplete or fast typing)
  if (value.length > 1) {
    // Take only the last digit typed
    value = value.slice(-1)
  }

  // Update the digits array
  digits.value[index] = value

  // Update the input field
  input.value = value

  // Emit the updated value
  const code = digits.value.join("")
  emit("update:modelValue", code)

  // Auto-focus logic
  if (value && index < props.length - 1) {
    // Move to next input
    nextTick(() => {
      inputs.value[index + 1]?.focus()
    })
  }

  // Check if complete
  if (code.length === props.length && !code.includes("")) {
    emit("complete", code)
  }
}

const handleKeyDown = (index, event) => {
  const input = event.target

  if (event.key === "Backspace") {
    event.preventDefault()

    if (digits.value[index]) {
      // Clear current digit
      digits.value[index] = ""
      input.value = ""
      emit("update:modelValue", digits.value.join(""))
    } else if (index > 0) {
      // Move to previous input and clear it
      digits.value[index - 1] = ""
      nextTick(() => {
        if (inputs.value[index - 1]) {
          inputs.value[index - 1].value = ""
          inputs.value[index - 1].focus()
        }
      })
      emit("update:modelValue", digits.value.join(""))
    }
  } else if (event.key === "Delete") {
    event.preventDefault()
    digits.value[index] = ""
    input.value = ""
    emit("update:modelValue", digits.value.join(""))
  } else if (event.key === "ArrowLeft" && index > 0) {
    event.preventDefault()
    inputs.value[index - 1]?.focus()
  } else if (event.key === "ArrowRight" && index < props.length - 1) {
    event.preventDefault()
    inputs.value[index + 1]?.focus()
  } else if (event.key === "Enter") {
    const code = digits.value.join("")
    if (code.length === props.length && !code.includes("")) {
      emit("complete", code)
    }
  } else if (event.key.length === 1 && /\d/.test(event.key)) {
    // Clear the field before the input event fires
    input.value = ""
  }
}

const handlePaste = (event) => {
  event.preventDefault()
  const paste = event.clipboardData.getData("text")
  const numbers = paste.replace(/\D/g, "").slice(0, props.length)

  if (numbers) {
    // Fill in the digits
    const newDigits = numbers.split("").concat(Array(props.length).fill("")).slice(0, props.length)
    digits.value = newDigits

    // Update all input fields
    nextTick(() => {
      inputs.value.forEach((input, idx) => {
        if (input) input.value = newDigits[idx] || ""
      })

      // Focus the last filled input or the first empty one
      const lastIndex = Math.min(numbers.length, props.length - 1)
      inputs.value[lastIndex]?.focus()
    })

    // Emit the update
    const code = digits.value.join("")
    emit("update:modelValue", code)

    if (numbers.length === props.length) {
      emit("complete", numbers)
    }
  }
}

// Focus first input on mount
onMounted(() => {
  nextTick(() => {
    inputs.value[0]?.focus()
  })
})
</script>
